import { type FC } from "react";
import { useTranslation } from "react-i18next";
import type { Stream, StreamContent } from "@/partner/entities/streams";
import {
  getActiveStreamSignal,
  useStreamMonitoringQuery,
} from "@/partner/entities/streams";
import {
  LiveInputMonitoringView,
  useMonitoringPeriod,
} from "@/partner/features/liveInputMonitoring";
import { StreamDialogHeader } from "./StreamDialogHeader";

interface StreamMonitoringTabProps {
  content: StreamContent;
  stream: Stream;
  onClose: () => void;
}

// Окно «Этот эфир»: от выхода в эфир (startedAt, иначе первое подключение
// энкодера lastPublishAt) до finishAt, у идущего эфира до «сейчас».
export const StreamMonitoringTab: FC<StreamMonitoringTabProps> = ({
  content,
  stream,
  onClose,
}) => {
  const { t } = useTranslation();
  const broadcastFrom = stream.startedAt ?? stream.lastPublishAt ?? null;
  const broadcast = broadcastFrom
    ? { from: broadcastFrom, to: stream.finishAt ?? null }
    : null;
  const { period, setPeriod, range, periods } = useMonitoringPeriod({
    live: stream.liveConnected,
    broadcast,
  });
  // Каждый тик меняет from/to, и Apollo на время загрузки отдаёт data =
  // undefined: показываем previousData, чтобы графики и чип статуса не
  // мигали скелетонами раз в 30 с. no-cache: иначе на Stream в кеше
  // копился бы новый monitoring({from,to}) на каждый тик.
  const { data, previousData, loading, error } = useStreamMonitoringQuery({
    variables: { contentId: content.id, from: range.from, to: range.to },
    fetchPolicy: "no-cache",
  });
  const shown = data ?? previousData;
  return (
    <>
      <StreamDialogHeader
        title={t("streams.form.dialogTitle")}
        onClose={onClose}
      />
      <LiveInputMonitoringView
        monitoring={shown?.content?.stream?.monitoring}
        loading={loading && !previousData}
        error={error}
        period={period}
        periods={periods}
        onChangePeriod={setPeriod}
        viewersOnline={shown?.content?.viewersOnline ?? null}
        signal={getActiveStreamSignal(stream)}
      />
    </>
  );
};
